import { Component, DestroyRef, OnInit, Input, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TaskDataService } from "../../services/TaskDataService";
import { ControlDataService } from "@app/controls/services/control.data.service";
import { GenericGroupedListConfig } from "@app/shared/models/GenericList/GenericGroupedListConfig";
import { EffectivenessControlOverviewModel } from "@app/controls/testing/advanced/control-overview/shared/effectiveness-control-overview.model";
import { IdNameColorModel } from "@models/generic/IdNameColorModel";
import { TabService } from "@services/tabs/TabService";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { from, of } from "rxjs";
import { nameof } from "@methods/jeffs-toolkit";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/tasks/i18n/scope";
import { GenericGroupedListManagerComponent } from "@app/shared/generic-managers/generic-grouped-list-manager/generic-grouped-list-manager.component";

@Component({
    selector: "task-effectiveness",
    templateUrl: "./task-effectiveness.component.html",
    imports: [GenericGroupedListManagerComponent],
    viewProviders: [provideTranslationScope(scope)]
})
export class TaskEffectivenessComponent implements OnInit {
    protected _ds = inject(TaskDataService);
    private _controlDataService = inject(ControlDataService);
    private tabService = inject(TabService);
    private pages = inject(Pages);
    private _translation = injectTranslation(scope);
    private _destroyRef = inject(DestroyRef);

    @Input() id: string;

    effectivenessOverviewConfig: GenericGroupedListConfig;
    scores: IdNameColorModel[];

    async ngOnInit(): Promise<void> {
        this._controlDataService.getAllScores().subscribe((d) => {
            this.scores = d;

            // The list-config strings are read once, outside change detection, so they
            // cannot go through the translate pipe. Build the config once the scope is ready.
            from(this._translation.ready())
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(() => {
                    this.createOverviewConfig((key: string) => this._translation.t(key));
                });
        });
    }

    createOverviewConfig(t: (key: string) => string): void {
        this.effectivenessOverviewConfig = <GenericGroupedListConfig>{
            name: t("testPlansConfigName"),
            groupByProp: nameof<EffectivenessControlOverviewModel>((eff) => eff.groupId),
            groupByDisplayName: nameof<EffectivenessControlOverviewModel>((eff) => eff.groupName),
            overviewSortBy: nameof<EffectivenessControlOverviewModel>((eff) => eff.endDate),
            overviewSortAsc: true,

            idProp: nameof<EffectivenessControlOverviewModel>((eff) => eff.guid),

            fields: [
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.testPlanName),
                    prettyName: t("testPlanNameColumn"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.statusDescription
                    ),
                    prettyName: t("columnStatus"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.startDate),
                    prettyName: t("startDateColumn"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.endDate),
                    prettyName: t("endDateColumn"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.numberOfSamples
                    ),
                    prettyName: t("samplesColumn"),
                    fieldType: GenericListFieldType.Number,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.score),
                    prettyName: t("scoreLabel"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    getDataMethod: () => {
                        return of(this.scores);
                    },
                },
            ],

            cellConfig: {
                testPlanName: {
                    alignLeft: true,
                },
            },

            cellSummaries: {
                testPlanName: {
                    cellStringFormat: t("testPlanGroupSummaryFormat"),
                    embedGroupName: true,
                    embedCount: true,
                },
                startDate: {
                    cellStringFormat:
                        "{{" +
                        nameof<EffectivenessControlOverviewModel>((eff) => eff.groupStartDate) +
                        "}}",
                },
                endDate: {
                    cellStringFormat:
                        "{{" +
                        nameof<EffectivenessControlOverviewModel>((eff) => eff.groupEndDate) +
                        "}}",
                },
                numberOfSamples: {
                    cellStringFormat: "[embedSum]/[embedAvg]",
                    embedSum: true,
                    embedAvg: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.groupMinimumSampleCount
                    ), // This will be minimum sample count in future.
                },
                score: {
                    embedColor: { colorCol: "groupScoreColor", textCol: "groupScoreText" },
                },
            },

            overviewRowActionsName: t("details"),
            overviewRowActions: [
                {
                    icon: "fas fa-edit",
                    tooltip: t("openTestPlanDetailsTooltip"),
                    clickEvent: (row: EffectivenessControlOverviewModel) => {
                        this.tabService.generateTab(
                            this.pages.ControlsTestingAdvEffDetail,
                            row.guid
                        );
                    },
                },
            ],
            editOverride: (row: any) => {
                this.tabService.generateTab(this.pages.ControlsTestingAdvEffDetail, row.guid);
                return false;
            },
            dataMethod: () => this._ds.getTaskEffectivenesses(this.id, this.scores),
        };
    }
}
